/**
 * Timezone Utility
 * 
 * Centralizes all timezone-related operations for the booking system.
 * All timestamps and time slots should use America/Los_Angeles timezone.
 */

import { toZonedTime, fromZonedTime, format } from 'date-fns-tz';

// Application timezone constant
export const APP_TIMEZONE = 'America/Los_Angeles';

/**
 * Convert any Date to LA timezone
 */
export function toLATime(date: Date): Date {
  return toZonedTime(date, APP_TIMEZONE);
}

/**
 * Convert LA timezone date to UTC
 */
export function fromLATime(date: Date): Date {
  return fromZonedTime(date, APP_TIMEZONE);
}

/**
 * Get current time in LA timezone
 */
export function nowInLA(): Date {
  return toLATime(new Date());
}

/**
 * Format date in LA timezone
 */
export function formatInLA(date: Date, formatStr: string): string {
  return format(toLATime(date), formatStr, { timeZone: APP_TIMEZONE });
}

/**
 * Create a Date object in LA timezone from date components
 */
export function createLADate(
  year: number,
  month: number,
  day: number,
  hour: number = 0,
  minute: number = 0,
  second: number = 0
): Date {
  // Create date string in LA timezone format
  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  
  // Parse as LA time and return as Date
  return fromZonedTime(dateStr, APP_TIMEZONE);
}

/**
 * Get start of day in LA timezone
 */
export function getStartOfDayLA(date: Date): Date {
  const laDate = toLATime(date);
  laDate.setHours(0, 0, 0, 0);
  return fromLATime(laDate);
}

/**
 * Get end of day in LA timezone
 */
export function getEndOfDayLA(date: Date): Date {
  const laDate = toLATime(date);
  laDate.setHours(23, 59, 59, 999);
  return fromLATime(laDate);
}

/**
 * Check if a date is the same day in LA timezone
 */
export function isSameDayLA(date1: Date, date2: Date): boolean {
  const la1 = toLATime(date1);
  const la2 = toLATime(date2);
  
  return (
    la1.getFullYear() === la2.getFullYear() &&
    la1.getMonth() === la2.getMonth() &&
    la1.getDate() === la2.getDate()
  );
}

/**
 * Parse time string (e.g., "2:00 PM") and combine with date in LA timezone
 * The date parameter is interpreted as a calendar date (year/month/day) 
 * regardless of its internal timezone representation
 * 
 * FIXED: Now properly handles dates created in different user timezones
 */
export function parseTimeStringLA(date: Date, timeString: string): Date {
  const [timePart, period] = timeString.split(' ');
  const [hourStr, minuteStr] = timePart.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr || '0', 10);

  // Convert to 24-hour format
  if (period === 'PM' && hour !== 12) {
    hour += 12;
  } else if (period === 'AM' && hour === 12) {
    hour = 0;
  }

  // FIXED: Convert the date to LA timezone first to get correct calendar date
  // This ensures we get the correct year/month/day regardless of user's timezone
  const laDate = toLATime(date);
  const year = laDate.getFullYear();
  const month = laDate.getMonth();
  const day = laDate.getDate();

  // Create new date with the specified time in LA timezone
  return createLADate(year, month, day, hour, minute, 0);
}

/**
 * Format time in LA timezone (e.g., "2:00 PM")
 */
export function formatTimeLA(date: Date): string {
  return formatInLA(date, 'h:mm a');
}

/**
 * Format date in LA timezone (e.g., "2025-12-15")
 */
export function formatDateLA(date: Date): string {
  return formatInLA(date, 'yyyy-MM-dd');
}

/**
 * Format datetime in LA timezone (e.g., "December 15, 2025 at 2:00 PM PST")
 */
export function formatDateTimeLA(date: Date): string {
  return formatInLA(date, 'MMMM d, yyyy \'at\' h:mm a zzz');
}

/**
 * Get day of week in LA timezone (0 = Sunday, 6 = Saturday)
 */
export function getDayOfWeekLA(date: Date): number {
  return toLATime(date).getDay();
}

/**
 * Add days in LA timezone context
 */
export function addDaysLA(date: Date, days: number): Date {
  const laDate = toLATime(date);
  laDate.setDate(laDate.getDate() + days);
  return fromLATime(laDate);
}
